import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQuery } from '@apollo/client';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Container, ShippingInfo, AlertMessage } from 'components';
import {
  authenticationState,
  loadingState,
  infoModalState,
  caseAssetsState,
  accountState,
} from 'state/atoms';
import { GET_RMA_CASE } from 'connectors/queries/rmaCases';
import { getShippingPrice, getItemPrice } from 'utils/helpers';
import {
  INTEGRATION_USER,
  CALIX_SUPPORT_USER_LABEL,
  NOT_APPLICABLE_LABEL,
  SERVICE_ADM_CASE_TYPE,
  HEADER_TOTAL_MESSAGE,
} from 'utils/constants';
import { CaseInfo, Title, DetailsColumn, AssetSection, AssetHeader } from './styledComponents';
import Attachments from './subcomponents/Attachments';
import Comments from './subcomponents/Comments';
import CommentSender from './subcomponents/CommentSender';
import Tabs from 'components/Tabs';
import OrdersTable from './subcomponents/OrdersTable';
import { ItemsTable } from 'components/tables/ItemsTable';
import { Tooltip } from '@Calix-Commerce/design-system/components';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';

export const getItemsPrice = (items) => {
  return items.reduce((total, item) => total + getItemPrice(item), 0);
};

const readCase = (data) => data.getAccount.getRmaCaseByNumber;

const CaseDetails = () => {
  const { getDefaultBillToAddress, currencyConversionRate } = useRecoilValue(accountState);
  const setAssetState = useSetRecoilState(caseAssetsState);
  const { contactId: sessionContactId, endCustomerAccountId } = useRecoilValue(authenticationState);
  const setLoading = useSetRecoilState(loadingState);
  const setInfoModal = useSetRecoilState(infoModalState);
  const [caseDetails, setCaseDetails] = useState(null);
  const [selected, setSelected] = useState(0);
  const { caseNumber } = useParams();
  const history = useHistory();
  const location = useLocation();
  const formatPrice = useFormatPrice();
  const rmaOrderParam = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const rmaOrderFromUrl = urlParams.get('rmaOrder');
    const salesOrders = (caseDetails && caseDetails.salesOrders) || [];

    if (salesOrders.includes(rmaOrderFromUrl)) {
      setSelected(salesOrders.findIndex((order) => order === rmaOrderFromUrl) + 1);

      return rmaOrderFromUrl;
    }

    return null;
  }, [caseDetails, location.search, setSelected]);

  const { loading } = useQuery(GET_RMA_CASE, {
    variables: {
      accountId: endCustomerAccountId,
      input: {
        caseNumber,
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setAssetState({
        comments: [],
        attachments: readCase(data).attachments,
        relationships: readCase(data).attachmentCommentRelationships,
      });

      const caseDetails = readCase(data) || {};

      const salesOrders =
        (caseDetails.rmaNumber &&
          caseDetails.rmaNumber
            .split(/[^A-Za-z0-9]/g)
            .filter((order) => order)
            .sort((a, b) => a - b)) ||
        [];

      setCaseDetails({ ...caseDetails, salesOrders });
    },
    onError: (error) => {
      setInfoModal({
        open: true,
        config: {
          title: 'Case Inaccessible',
          content:
            'We couldn’t get the details for the case you are looking for. Please contact the Calix RMA team.',
        },
      });
      history.push('/');
    },
  });

  const scrollTo = useCallback((elementId) => {
    const scrollableElement = document.getElementById(elementId);

    if (scrollableElement) {
      scrollableElement.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (rmaOrderParam && caseDetails) {
      scrollTo('tabs');
    }
  }, [scrollTo, rmaOrderParam, caseDetails]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  if (loading || !caseDetails) {
    return null;
  }

  const {
    id,
    items,
    status,
    owner,
    currencyCode,
    caseType,
    resolutionSummary,
    createdDate,
    lastModifiedDate,
    contactId: caseContactId,
    contactName,
    poNumber,
    salesOrders = [],
    problemDescription = '',
    shipping = {},
  } = caseDetails;
  const shippingPrice = getShippingPrice(
    shipping.method,
    getDefaultBillToAddress,
    currencyConversionRate
  );
  const itemsPrice = getItemsPrice(items);
  const isServiceAdministrationCase = caseType === SERVICE_ADM_CASE_TYPE;

  const totalPrice = shippingPrice * items.length + itemsPrice;

  // Split all non-alphanumeric characters and make sure
  // empty orders are ignored (happens when two special chars happen together)

  const renderRmaNumber = (salesOrders) => {
    if (!salesOrders.length) {
      return null;
    }

    // Split all non-alphanumeric characters and make sure
    // empty orders are ignored (happens when two special chars happen together)

    return salesOrders.map((salesOrder, index) => (
      <React.Fragment key={salesOrder}>
        <a
          href={`?rmaOrder=${salesOrder}`}
          onClick={(event) => {
            event.preventDefault();
            history.push(`?rmaOrder=${salesOrder}`);
            setSelected(index + 1);
          }}
        >
          {salesOrder}
        </a>

        {index + 1 < salesOrders.length ? ', ' : null}
      </React.Fragment>
    ));
  };

  const isCaseContact = sessionContactId.substr(0, 15) === caseContactId.substr(0, 15);

  return (
    <Container>
      <Title>RMA Detail</Title>
      <CaseInfo>
        <DetailsColumn>
          <span>
            Case #:{' '}
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                history.push(location.pathname);
                scrollTo('tabs');
                setSelected(0);
              }}
            >
              {caseNumber}
            </a>
          </span>
          <span>RMA #: {renderRmaNumber(salesOrders) || NOT_APPLICABLE_LABEL}</span>
          <span>Created Date: {new Date(createdDate).toLocaleDateString()}</span>
          <span>Last Modified Date: {new Date(lastModifiedDate).toLocaleDateString()}</span>
        </DetailsColumn>
        <DetailsColumn alignRight>
          {!isServiceAdministrationCase && (
            <>
              <span>
                Total: {formatPrice(totalPrice, currencyCode)}
                <Tooltip content={HEADER_TOTAL_MESSAGE} placement="top" />
              </span>
            </>
          )}
          <span>Status: {status}</span>
          <span>Transaction Currency: {currencyCode}</span>
          <span>Case Owner: {owner !== INTEGRATION_USER ? owner : CALIX_SUPPORT_USER_LABEL}</span>
          <span>Contact: {contactName}</span>
        </DetailsColumn>
      </CaseInfo>
      {resolutionSummary && (
        <AlertMessage icon={false} severity="warning">
          <b>Resolution Summary:</b> {resolutionSummary}
        </AlertMessage>
      )}
      {!isServiceAdministrationCase && (
        <>
          <ShippingInfo shipping={shipping} poNumber={poNumber} />
          <div id="tabs">
            <Tabs selected={selected} onChange={setSelected}>
              <Tabs.Tab label="Case" onClick={() => history.push(location.pathname)}>
                <ItemsTable
                  items={items}
                  shippingPrice={shippingPrice}
                  shippingMethod={shipping.method}
                  currencyCode={currencyCode}
                />
              </Tabs.Tab>

              {salesOrders &&
                salesOrders.map((order) => (
                  <Tabs.Tab
                    key={order}
                    label={`RMA ${order}`}
                    onClick={() => history.push(`?rmaOrder=${order}`)}
                  >
                    <OrdersTable order={order} accountId={endCustomerAccountId} />
                  </Tabs.Tab>
                ))}
            </Tabs>
          </div>
        </>
      )}
      {isServiceAdministrationCase && (
        <AssetSection>
          <AssetHeader>Problem Description</AssetHeader>
          <div style={{ padding: '25px', whiteSpace: 'pre-wrap' }}>{problemDescription}</div>
        </AssetSection>
      )}
      <AssetSection>
        <AssetHeader>Comments</AssetHeader>
        {isCaseContact ? <CommentSender caseId={id} /> : null}
        <Comments caseId={id} caseNumber={caseNumber} />
      </AssetSection>
      {isCaseContact ? (
        <AssetSection>
          <AssetHeader>Attachments</AssetHeader>
          <Attachments />
        </AssetSection>
      ) : null}
    </Container>
  );
};

export { CaseDetails };
